/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { useEffect, useRef } from 'react';

import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { getTrialDaysLeft } from '@confluence/trial-components/entry-points/getTrialDaysLeft';
import { useTransitionId } from '@confluence/route-manager/entry-points/RouteState';
import { fg } from '@confluence/feature-gating';

import type { usePersistentInviteButtonEligibleLicenseDetailsQuery as usePersistentInviteButtonEligibleLicenseDetailsQuery$data } from './__types__/usePersistentInviteButtonEligibleLicenseDetailsQuery';

export function usePersistentInviteButtonEligible() {
	const { edition, isLicensed, isLoggedIn } = useSessionData();

	const isFreeEdition = edition === ConfluenceEdition.FREE;
	const skipLicenseQuery = isFreeEdition || !isLoggedIn || !isLicensed;
	const {
		refetch,
		data: licenseDetailsData,
		loading: licenseDetailsLoading,
		error: licenseDetailsDataError,
	} = useQuery<usePersistentInviteButtonEligibleLicenseDetailsQuery$data>(
		gql`
			query usePersistentInviteButtonEligibleLicenseDetailsQuery {
				license {
					licenseConsumingUserCount
					trialEndDate
				}
			}
		`,
		{
			skip: skipLicenseQuery,
		},
	);
	const trialDaysLeft = fg('tako-4040_connie_trial_days_left_standardisation')
		? getTrialDaysLeftFromTrialEndDate(licenseDetailsData?.license?.trialEndDate)
		: getTrialDaysLeft(licenseDetailsData?.license?.trialEndDate);
	const isInTrial = trialDaysLeft ? trialDaysLeft > 0 : false;

	// Sometimes, the trial end date is not set on the initial load for a new tenant, even though they have been put into a trial.
	// This can lead to the invite button not appearing. To mitigate this, we refetch the license details on page transition if we
	// suspect the user is in a trial, but the trial end date is null.
	const transitionId = useTransitionId();
	const hasRefetched = useRef(false);
	const initialTransitionId = useRef(transitionId);
	useEffect(() => {
		if (!fg('cc_persistent_invite_button_refetch_on_transition')) {
			return;
		}

		if (isInTrial || skipLicenseQuery || licenseDetailsLoading || licenseDetailsDataError) {
			return;
		}

		const trialSuspected = Boolean(
			licenseDetailsData?.license?.trialEndDate === null &&
				licenseDetailsData?.license?.licenseConsumingUserCount === 1 &&
				!isFreeEdition,
		);

		if (!hasRefetched.current && trialSuspected && transitionId > initialTransitionId.current) {
			void refetch();
			hasRefetched.current = true;
		}
	}, [
		edition,
		isFreeEdition,
		isInTrial,
		isLicensed,
		isLoggedIn,
		licenseDetailsData,
		licenseDetailsDataError,
		licenseDetailsLoading,
		refetch,
		skipLicenseQuery,
		transitionId,
	]);

	return {
		loading: licenseDetailsLoading,
		error: licenseDetailsDataError,
		isEligible: isInTrial || isFreeEdition,
	};
}
